// Communication service client for StratIQX onboarding application
// Handles API calls to the StratIQX communication service

import { ContactFormData } from '../types/support.types'
import { CustomIndustryRequestData } from '../features/onboarding/components/CustomIndustryRequestModal'

const COMMUNICATION_API_URL = '/api/communication/send'
const SERVICE_NAME = 'stratiqx-onboarding'

// Admin token for service-to-service communication
// Updated token that matches the communication service configuration
const ADMIN_TOKEN = 'stratiqx-admin-88de24b7538d03f265d4e94cd96cd055478c76fe3bf259f2e9dd611cf143f8c8'

export interface CommunicationPayload {
  communication_type: string
  user_id: string
  email: string
  name?: string
  [key: string]: any
}

export interface CommunicationResponse {
  message?: string
  error?: {
    name: string
    message: string
    code?: string
  }
}

export class CommunicationServiceError extends Error {
  public readonly code?: string
  public readonly statusCode?: number

  constructor(message: string, code?: string, statusCode?: number) {
    super(message)
    this.name = 'CommunicationServiceError'
    this.code = code
    this.statusCode = statusCode
  }
}

/**
 * Communication service client class
 */
export class CommunicationService {
  private readonly apiUrl: string
  private readonly serviceName: string
  private readonly adminToken: string

  constructor(
    apiUrl = COMMUNICATION_API_URL,
    serviceName = SERVICE_NAME,
    adminToken = ADMIN_TOKEN
  ) {
    this.apiUrl = apiUrl
    this.serviceName = serviceName
    this.adminToken = adminToken
  }

  /**
   * Send communication request to the service
   */
  private async sendRequest(payload: CommunicationPayload): Promise<CommunicationResponse> {
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.adminToken}`,
          'X-Service': this.serviceName
        },
        body: JSON.stringify(payload)
      })

      const data = await response.json() as CommunicationResponse

      if (!response.ok) {
        throw new CommunicationServiceError(
          data.error?.message || `HTTP ${response.status}: ${response.statusText}`,
          data.error?.code,
          response.status
        )
      }

      return data
    } catch (error) {
      if (error instanceof CommunicationServiceError) {
        throw error
      }

      // Handle network errors, etc.
      throw new CommunicationServiceError(
        error instanceof Error ? error.message : 'Failed to send communication request'
      )
    }
  }

  /**
   * Send support notification email to the support team
   */
  async sendSupportNotification(contactData: ContactFormData): Promise<void> {
    const payload: CommunicationPayload = {
      communication_type: 'support_email',
      user_id: `support_request_${Date.now()}`,
      email: 'support@stratiqx.ai',
      name: 'StratIQX Support',
      contact_name: contactData.name || '',
      contact_email: contactData.email,
      contact_subject: contactData.subject,
      contact_message: contactData.message,
      submitted_at: new Date().toISOString(),
      user_agent: navigator.userAgent,
      source_page: window.location.pathname,
      session_id: this.generateSessionId()
    }

    await this.sendRequest(payload)
  }

  /**
   * Send confirmation email to the user
   */
  async sendUserConfirmation(contactData: ContactFormData): Promise<void> {
    const payload: CommunicationPayload = {
      communication_type: 'contact_email',
      user_id: `user_contact_${Date.now()}`,
      email: contactData.email,
      name: contactData.name || 'Valued Customer',
      message: `We've received your inquiry about "${contactData.subject}" and will respond within 24-48 hours.`
    }

    await this.sendRequest(payload)
  }

  /**
   * Send both support notification and user confirmation
   */
  async sendContactFormSubmission(contactData: ContactFormData): Promise<void> {
    // Send both emails concurrently for better performance
    await Promise.all([
      this.sendSupportNotification(contactData),
      this.sendUserConfirmation(contactData)
    ])
  }

  /**
   * Send custom industry request notification to support team
   */
  async sendCustomIndustryNotification(industryData: CustomIndustryRequestData): Promise<void> {
    const payload: CommunicationPayload = {
      communication_type: 'support_email',
      user_id: `industry_request_${Date.now()}`,
      email: 'support@stratiqx.ai',
      name: 'StratIQX Support',
      contact_name: industryData.companyName,
      contact_email: industryData.contactEmail,
      contact_subject: `Custom Industry Request: ${industryData.industryName}`,
      contact_message: this.formatIndustryRequestMessage(industryData),
      submitted_at: new Date().toISOString(),
      user_agent: navigator.userAgent,
      source_page: window.location.pathname,
      session_id: this.generateSessionId(),
      // Additional industry-specific fields
      industry_name: industryData.industryName,
      business_description: industryData.businessDescription,
      specific_needs: industryData.specificNeeds,
      company_size: industryData.companySize,
      urgency: industryData.urgency
    }

    await this.sendRequest(payload)
  }

  /**
   * Send custom industry confirmation to user
   */
  async sendCustomIndustryConfirmation(industryData: CustomIndustryRequestData): Promise<void> {
    const payload: CommunicationPayload = {
      communication_type: 'contact_email',
      user_id: `industry_user_${Date.now()}`,
      email: industryData.contactEmail,
      name: industryData.companyName || 'Valued Customer',
      message: `We've received your custom industry request for "${industryData.industryName}" and will review it within 1-2 business days. Our team will create a tailored intelligence configuration specifically for your business needs.`
    }

    await this.sendRequest(payload)
  }

  /**
   * Send both custom industry notification and user confirmation
   */
  async sendCustomIndustrySubmission(industryData: CustomIndustryRequestData): Promise<void> {
    // Send both emails concurrently for better performance
    await Promise.all([
      this.sendCustomIndustryNotification(industryData),
      this.sendCustomIndustryConfirmation(industryData)
    ])
  }

  /**
   * Format industry request data into a readable message for support team
   */
  private formatIndustryRequestMessage(data: CustomIndustryRequestData): string {
    return `CUSTOM INDUSTRY REQUEST

Company: ${data.companyName}
Industry: ${data.industryName}
Company Size: ${data.companySize || 'Not specified'}
Priority: ${data.urgency.toUpperCase()}

BUSINESS DESCRIPTION:
${data.businessDescription}

SPECIFIC INTELLIGENCE NEEDS:
${data.specificNeeds}

Please create a custom industry configuration based on these requirements and respond to the customer within the specified timeline.`
  }


  /**
   * Send welcome email to user
   */
  async sendWelcomeEmail(userEmail: string, userName: string): Promise<void> {
    const payload: CommunicationPayload = {
      communication_type: 'welcome_email',
      user_id: `welcome_${Date.now()}`,
      email: userEmail,
      name: userName
    }

    await this.sendRequest(payload)
  }

  /**
   * Send payment confirmation email to user
   */
  async sendPaymentConfirmation(
    userEmail: string, 
    userName: string, 
    amount: string | number,
    serviceName: string,
    transactionId?: string
  ): Promise<void> {
    const payload: CommunicationPayload = {
      communication_type: 'payment_email',
      user_id: `payment_${Date.now()}`,
      email: userEmail,
      name: userName,
      amount: amount,
      date: new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long', 
        day: 'numeric'
      }),
      transactionId: transactionId || `txn_${Date.now().toString().slice(-6)}`,
      service_name: serviceName
    }

    await this.sendRequest(payload)
  }

  /**
   * Send both welcome and payment confirmation emails for service activation
   */
  async sendServiceActivationEmails(
    userEmail: string, 
    userName: string,
    serviceName: string,
    amount: string | number = 'FREE',
    transactionId?: string
  ): Promise<void> {
    // Send both emails concurrently for better performance
    await Promise.all([
      this.sendWelcomeEmail(userEmail, userName),
      this.sendPaymentConfirmation(userEmail, userName, amount, serviceName, transactionId)
    ])
  }

  /**
   * Generate a simple session ID for tracking
   */
  private generateSessionId(): string {
    return `sess_${Math.random().toString(36).substr(2, 9)}_${Date.now()}`
  }
}

// Export a default instance
export const communicationService = new CommunicationService()